define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-style",
	"dojo/on",
	"dojo/topic",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"csi/message",
	"csi/html",
	"csi/locale",
	"dojo/text!csi/templates/Footer.html"
], function(declare, lang, domStyle, on, topic, _TemplatedMixin, _WidgetBase, message, html, locale, template) {

	// Boolean: indicates if currently showing progress message
	var _bProgress = false;

	// String: configuration status
	var _configStatus = null;

	// String: indicates method to use to show configuration status
	var _configMethod = null;

	// String: error message from configuration if any
	var _configErrorMessage = null;

	// String: Java stack trace from configuration if any
	var _configErrorException = null;

	function formatTuple(/*FormattingTuple*/formattingTuple) {
		if (formattingTuple == null)
			return null;
		return locale.parameterResource(formattingTuple.localizationId, formattingTuple.parameters);
	}

	// Javascript implementation of Java method:
	// com.centricsoftware.pi.util.progress.IProgressStep.writeHierarchicalMessage
	function writeHierarchicalMessage(/*String[]*/appendable, /*ProgressStep*/root, /*String*/levelSeparator, /*String*/siblingSeparator) {
		var message = formatTuple(root.formattingTuple);
		if (message)
			appendable.push(message);

		var steps = root.currentSteps;
		if (steps != null && steps.length > 0) {
			if (message)
				appendable.push(levelSeparator);

			var needSiblingSeparator = false;
			steps.forEach(function(step) {
				if (needSiblingSeparator)
					appendable.push(siblingSeparator);
				writeHierarchicalMessage(appendable, step, levelSeparator, siblingSeparator);
				needSiblingSeparator = true;
			});
		}
	}

	// Get Hierarchical Message from Progress Step
	//TODO: move to locale?
	function getHierarchicalMessage(/*ProgressStep*/root) {
		var resultArray = [];
		writeHierarchicalMessage(resultArray, root, " - ", ", ");
		return resultArray.join("");
	}

	return declare([_WidgetBase, _TemplatedMixin], {
		templateString: template,

		postCreate: function() {
			// summary: subscribe to message topics
			this.own(
				topic.subscribe("/csi/configuration/refresh", lang.hitch(this, this._onConfiguration)),
				topic.subscribe("/csi/dfd/progress", lang.hitch(this, this._onDfdProgress)),
				topic.subscribe("/csi/message/error", lang.hitch(this, this._onError)),
				topic.subscribe("/csi/message/hide", lang.hitch(this, this._onHide)),
				topic.subscribe("/csi/message/progress", lang.hitch(this, this._onProgress)),
				topic.subscribe("/csi/message/success", lang.hitch(this, this._onSuccess)),
				topic.subscribe("/csi/servermessage/PROGRESS_EVENT", lang.hitch(this, this._onServerProgressEvent)),
				topic.subscribe("/csi/message/showInfo", lang.hitch(this, this._showInfo)),
				topic.subscribe("/csi/message/hideInfo", lang.hitch(this, this._hideInfo)),
				topic.subscribe("/csi/hash/start", lang.hitch(this, this._onCsiHash))
			);

			this.own(
				on(this.statusNode, "click", lang.hitch(this, this._onStatusNodeClick))
			);
		},

		_onConfiguration: function(/*String*/s, /*Boolean*/bProgress, /*Boolean*/bError, /*String*/errorMessage, /*String*/errorException) {
			// summary: update message based on configuration status
			if (s != _configStatus) {
				_configStatus = s;
				_configMethod = bError ? "_onError" : (bProgress ? "_onProgress" : "_onSuccess");
				_configErrorMessage = errorMessage;
				_configErrorException = errorException;
				this._hide();

				// if update complete, then clear message after displaying once
				if (!bError && !bProgress) {
					_configStatus = null;
				}
			}
		},

		_onDfdProgress: function(/*String*/s) {
			// summary: handle progress messages from deferred tracking
			if (s) {
				_bProgress = true;
				this._show(s, "yellow");
			} else if (_bProgress) {
				this._hide();
			}
		},

		_onError: function(/*String*/s) {
			// summary: display error message until another message is shown or until it is clicked
			if (s) {
				_bProgress = false;
				this._show(s, "red", "red");
			}
		},

		_onHide: function(/*Boolean*/bProgressOnly) {
			// summary: hide status indicator
			if (!bProgressOnly || _bProgress) {
				this._hide();
			}
		},

		_onProgress: function(/*String*/s) {
			// summary: display progress indicator with message
			if (s) {
				_bProgress = true;
				this._show(s, "yellow");
			}
		},

		_onSuccess: function(/*String*/s) {
			// summary: display message and automatically hide in 2 seconds
			_bProgress = false;

			if (s) {
				this._show(s, "lime");
			} else {
				this._hide();
			}
		},

		_onServerProgressEvent: function(/*Object*/serverMessage) {
			var currentProgress = serverMessage.currentProgress;
			var currentMessage = getHierarchicalMessage(serverMessage);
			if (currentProgress < 1) {
				var messageToDisplay = Math.round(currentProgress * 100.0) + "%. " + currentMessage;
				console.log("Server progress message: ", messageToDisplay);
				this._onProgress(messageToDisplay);
			}
		},

		_onStatusNodeClick: function() {
			// summary: show error dialog if the error occurred during update configuration
			if (_configErrorMessage) {
				message.errorDialog({
					exception: _configErrorException,
					message: _configErrorMessage
				});
			}
		},

		_hide: function() {
			// summary: revert to showing copyright message or configuration status
			_bProgress = false;

			if (_configStatus) {
				this[_configMethod](_configStatus);
			} else {
				html.setVisible(this.messageNode, false);
				html.setVisible(this.infoNode, true);
				html.setVisible(this.versionNode, true);
			}

			// when message is hidden, it is possible another message could go back up
			topic.publish("/csi/message/hidden");
		},

		_show: function(/*String*/text, /*String*/statusColor, /*String*/textColor) {
			// summary: show status message
			html.setVisible(this.versionNode, false);
			html.setVisible(this.infoNode, false);
			html.setVisible(this.messageNode, true);

			domStyle.set(this.textNode, "color", textColor || "");
			domStyle.set(this.statusNode, {
				backgroundColor: statusColor || "",
				visibility: statusColor ? "visible" : "hidden"
			});

			if (_bProgress && (text.indexOf("...") < 0)) {
				text += "...";
			}

			html.clear(this.textNode);
			html.text(this.textNode, text);
		},

		_showInfo: function(/*String*/s) {
			// Summary: Show info related to the current page. 
			// Info will be hidden once user navigates to another tab.
			// Use view.onTabSwitch() to restore info once user navigates back to the tab 
			html.clear(this.infoTextNode);
			if (s) {
				html.text(this.infoTextNode, s);
			}
		},

		_hideInfo: function() {
			// summary: hide and delete info message
			html.clear(this.infoTextNode);
		},

		_onCsiHash: function() {
			// summary: hide info message every time then user navigate to another tab
			this._hideInfo();
		}
	});
});